/*
 * Primefone v2 — Theme JS (vanilla, no framework)
 * Provides: scroll reveal, sticky header shadow, mobile menu, simple slider.
 */
(function () {
  'use strict';

  var prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---- Scroll Reveal -------------------------------------------------------
  const revealEls = document.querySelectorAll('[data-pf-reveal]');
  if (revealEls.length && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: '0px 0px -80px 0px', threshold: 0.05 });
    revealEls.forEach((el) => io.observe(el));
  } else {
    revealEls.forEach((el) => el.classList.add('is-visible'));
  }

  // ---- Sticky Header shadow on scroll -------------------------------------
  const header = document.querySelector('[data-pf-header]');
  if (header) {
    const onScroll = () => {
      if (window.scrollY > 8) header.classList.add('is-scrolled');
      else header.classList.remove('is-scrolled');
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // ---- Mobile Menu Toggle --------------------------------------------------
  let menuOpener = null;
  let previousOverflow = '';
  function closeMobileMenu(drawer) {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if ('inert' in HTMLElement.prototype) drawer.inert = true;
    document.body.style.overflow = previousOverflow;
    document.querySelectorAll('[data-pf-menu-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    if (menuOpener) menuOpener.focus();
  }
  function openMobileMenu(drawer, opener, focusSearch) {
    menuOpener = opener;
    if (!drawer.classList.contains('is-open')) previousOverflow = document.body.style.overflow;
    drawer.classList.add('is-open');
    document.querySelectorAll('[data-pf-menu-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'true'));
    drawer.setAttribute('aria-hidden', 'false');
    if ('inert' in HTMLElement.prototype) drawer.inert = false;
    document.body.style.overflow = 'hidden';
    const target = drawer.querySelector(focusSearch ? 'input[type="search"]' : '[data-pf-menu-close]');
    if (target) target.focus();
  }
  document.querySelectorAll('[data-pf-menu-toggle]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const targetId = btn.getAttribute('aria-controls');
      const drawer = targetId ? document.getElementById(targetId) : null;
      if (!drawer) return;
      if (drawer.classList.contains('is-open')) { closeMobileMenu(drawer); return; }
      openMobileMenu(drawer, btn, false);
    });
  });
  document.querySelectorAll('[data-pf-search-open]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const drawer = document.getElementById(btn.getAttribute('aria-controls'));
      if (drawer) openMobileMenu(drawer, btn, true);
    });
  });

  document.querySelectorAll('[data-pf-menu-close]').forEach((btn) => {
    btn.addEventListener('click', () => {
      // Close button lives inside the drawer; the overlay lives outside it —
      // fall back to whichever drawer is currently open.
      const drawer = btn.closest('[data-pf-drawer]') || document.querySelector('[data-pf-drawer].is-open');
      if (!drawer) return;
      closeMobileMenu(drawer);
    });
  });
  document.addEventListener('keydown', (event) => {
    const drawer = document.querySelector('[data-pf-drawer].is-open');
    if (!drawer) return;
    // A modal opened from the drawer (condition comparison) handles its own Escape and Tab.
    if (document.querySelector('dialog[data-condition-dialog][open]')) return;
    if (event.key === 'Escape') { event.preventDefault(); closeMobileMenu(drawer); return; }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), summary, [tabindex="0"]'))
      .filter((el) => el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (!first) return;
    if (event.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !drawer.contains(document.activeElement))) {
      event.preventDefault(); first.focus();
    }
  });
  window.matchMedia('(min-width: 1024px)').addEventListener('change', (event) => {
    const drawer = document.querySelector('[data-pf-drawer].is-open');
    if (event.matches && drawer) closeMobileMenu(drawer);
  });

  // ---- Announcement bar rotation ------------------------------------------
  // Calm cross-fade every 5 s; the messages share one grid cell (no height jump). Paused on hover and focus.
  // prefers-reduced-motion keeps the first message. No aria-live: the rotation is not announced.
  document.querySelectorAll('[data-pf-announcement]').forEach((bar) => {
    const items = Array.from(bar.querySelectorAll('[data-pf-announcement-item]'));
    if (items.length <= 1 || prefersReduced) return;
    // Kernbeloftes (data-pf-mobile-only): vanaf 1024px staan ze alle vier naast elkaar, dan niet wisselen.
    const mobileOnly = bar.hasAttribute('data-pf-mobile-only');
    const mobile = window.matchMedia('(max-width: 1023px)');
    let idx = 0, hovered = false, focused = false;
    const show = (next) => {
      items[idx].classList.remove('is-active'); if (!mobileOnly) items[idx].setAttribute('aria-hidden', 'true');
      idx = next;
      items[idx].classList.add('is-active'); items[idx].removeAttribute('aria-hidden');
    };
    bar.addEventListener('mouseenter', () => { hovered = true; });
    bar.addEventListener('mouseleave', () => { hovered = false; });
    bar.addEventListener('focusin', () => { focused = true; });
    bar.addEventListener('focusout', (event) => { focused = bar.contains(event.relatedTarget); });
    setInterval(() => { if (mobileOnly && !mobile.matches) return; if (!hovered && !focused && !document.hidden) show((idx + 1) % items.length); }, Number(bar.dataset.pfInterval) || 5000);
  });

  // ---- Slideshow / Hero Slider --------------------------------------------
  document.querySelectorAll('[data-pf-slider]').forEach((slider) => {
    const slides = Array.from(slider.querySelectorAll('[data-pf-slide]'));
    const dots = Array.from(slider.querySelectorAll('[data-pf-slider-dot]'));
    const prev = slider.querySelector('[data-pf-slider-prev]');
    const next = slider.querySelector('[data-pf-slider-next]');
    const autoplay = prefersReduced ? 0 : parseInt(slider.dataset.pfAutoplay || '0', 10);
    let current = 0;
    let timer = null;

    const show = (i) => {
      current = (i + slides.length) % slides.length;
      slides.forEach((s, idx) => {
        s.classList.toggle('is-active', idx === current);
      });
      dots.forEach((d, idx) => d.classList.toggle('is-active', idx === current));
    };

    if (slides.length <= 1) { show(0); return; }
    show(0);

    if (prev) prev.addEventListener('click', () => { show(current - 1); restart(); });
    if (next) next.addEventListener('click', () => { show(current + 1); restart(); });
    dots.forEach((d, i) => d.addEventListener('click', () => { show(i); restart(); }));

    const start = () => { if (autoplay > 0) timer = setInterval(() => show(current + 1), autoplay); };
    const restart = () => { if (timer) clearInterval(timer); start(); };

    slider.addEventListener('mouseenter', () => { if (timer) clearInterval(timer); });
    slider.addEventListener('mouseleave', start);
    start();
  });

  // ---- Logo / brand marquee ------------------------------------------------
  // CSS handles animation; this is a placeholder hook in case we need pause-on-hover JS.

  // ---- Reduced-motion: pauzeer decoratieve autoplay-video's ----------------
  if (prefersReduced) {
    document.querySelectorAll('video[autoplay]').forEach(function (v) {
      try { v.removeAttribute('autoplay'); v.pause(); } catch (e) {}
    });
  }
})();


/* ===== Primefone v2 motion layer (appended) ===== */
/* =====================================================================
 * Primefone v2 — Premium Motion Layer (vanilla JS, geen libs)
 * Voeg dit toe AAN theme.js (binnen of na de bestaande IIFE), of laad als
 * los <script>. Idempotent: dubbel-init wordt voorkomen via dataset-vlaggen.
 * Re-init na Shopify section render via 'shopify:section:load'.
 * Niet-conflicterend met de bestaande [data-pf-reveal] reveal en met de
 * eigen count-up in stats-section.liquid (die elementen dragen
 * data-pf-count-target en worden hier bewust OVERGESLAGEN).
 * ===================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var HAS_IO = 'IntersectionObserver' in window;
  // Magneet alleen op echte cursor-apparaten (desktop, pointer fine).
  var FINE_POINTER = window.matchMedia &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  // Shared in-view observer-factory: roept cb(el) eenmalig aan bij in-view.
  function onInView(els, cb, rootMargin) {
    if (!HAS_IO) { els.forEach(cb); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { cb(e.target); io.unobserve(e.target); }
      });
    }, { rootMargin: rootMargin || '0px 0px -80px 0px', threshold: 0.05 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- 1) Split-word kop-reveal: data-pf-words --------------- */
  function initWords(root) {
    var heads = (root || document).querySelectorAll('[data-pf-words]');
    heads.forEach(function (el) {
      if (el.dataset.pfWordsInit) return;
      el.dataset.pfWordsInit = '1';

      if (REDUCED) { el.classList.add('is-visible', 'is-done'); return; }

      var stagger = parseInt(el.getAttribute('data-pf-words-stagger'), 10);
      if (isNaN(stagger)) stagger = 60; // ms tussen woorden

      // Splits alleen platte tekst-nodes in woorden; bestaande inline-markup
      // (bv. <br>, <span class="accent">) blijft intact als 1 "woord".
      var nodes = Array.prototype.slice.call(el.childNodes);
      var frag = document.createDocumentFragment();
      var wordIndex = 0;

      nodes.forEach(function (node) {
        if (node.nodeType === 3) { // tekstnode
          var parts = node.textContent.split(/(\s+)/);
          parts.forEach(function (part) {
            if (part === '') return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
            frag.appendChild(buildWord(part, wordIndex++));
          });
        } else if (node.nodeType === 1) { // element: behandel als 1 woord-blok
          var wrap = document.createElement('span');
          wrap.className = 'pf-word';
          var inner = document.createElement('span');
          inner.className = 'pf-word__i';
          inner.style.setProperty('--pf-word-delay', (wordIndex++ * stagger) + 'ms');
          inner.appendChild(node.cloneNode(true));
          wrap.appendChild(inner);
          frag.appendChild(wrap);
        }
      });

      function buildWord(text, i) {
        var wrap = document.createElement('span');
        wrap.className = 'pf-word';
        var inner = document.createElement('span');
        inner.className = 'pf-word__i';
        inner.style.setProperty('--pf-word-delay', (i * stagger) + 'ms');
        inner.textContent = text;
        wrap.appendChild(inner);
        return wrap;
      }

      el.textContent = '';
      el.appendChild(frag);

      onInView([el], function (t) {
        t.classList.add('is-visible');
        var inners = t.querySelectorAll('.pf-word__i');
        var last = inners[inners.length - 1];
        if (last) {
          last.addEventListener('transitionend', function once() {
            t.classList.add('is-done');
            last.removeEventListener('transitionend', once);
          });
        } else {
          t.classList.add('is-done');
        }
      });
    });
  }

  /* ---------- 2) Magnetische knoppen: data-pf-magnet ---------------- */
  function initMagnet(root) {
    var btns = (root || document).querySelectorAll('[data-pf-magnet]');
    btns.forEach(function (el) {
      if (el.dataset.pfMagnetInit) return;
      el.dataset.pfMagnetInit = '1';
      if (REDUCED || !FINE_POINTER) return; // op touch/coarse: niets

      // Sterkte: hoe ver de knop max. naar de cursor schuift (px).
      var strength = parseFloat(el.getAttribute('data-pf-magnet-strength'));
      if (isNaN(strength)) strength = 18;
      var inner = el.querySelector('.pf-magnet__inner'); // optioneel diepte-element
      var rect = null;
      var rafId = null;
      var tx = 0, ty = 0;

      function measure() { rect = el.getBoundingClientRect(); }

      function apply() {
        rafId = null;
        el.style.setProperty('--pf-mx', tx.toFixed(2) + 'px');
        el.style.setProperty('--pf-my', ty.toFixed(2) + 'px');
        if (inner) {
          el.style.setProperty('--pf-mx-inner', (tx * 0.4).toFixed(2) + 'px');
          el.style.setProperty('--pf-my-inner', (ty * 0.4).toFixed(2) + 'px');
        }
      }
      function schedule() { if (rafId === null) rafId = requestAnimationFrame(apply); }

      el.addEventListener('pointerenter', function () {
        measure();
        el.classList.add('is-magnet-active');
      });
      el.addEventListener('pointermove', function (ev) {
        if (!rect) measure();
        var relX = (ev.clientX - rect.left) / rect.width - 0.5;  // -0.5..0.5
        var relY = (ev.clientY - rect.top) / rect.height - 0.5;
        tx = relX * strength * 2;
        ty = relY * strength * 2;
        schedule();
      });
      el.addEventListener('pointerleave', function () {
        el.classList.remove('is-magnet-active'); // terug via tragere transition
        tx = 0; ty = 0;
        schedule();
      });
      // herbereken na scroll/resize zodat de offset klopt
      window.addEventListener('resize', function () { rect = null; }, { passive: true });
    });
  }

  /* ---------- 3) Count-up getallen: data-pf-count ------------------- */
  function initCount(root) {
    var els = (root || document).querySelectorAll('[data-pf-count]');
    els.forEach(function (el) {
      if (el.dataset.pfCountInit) return;
      // Elementen van de dedicated stats-section dragen data-pf-count-target
      // en worden daar al geanimeerd -> hier overslaan (geen dubbele animatie).
      if (el.hasAttribute('data-pf-count-target')) return;

      // Spec-vorm: getal staat in de attribuutwaarde, bv. data-pf-count="98".
      var raw = el.getAttribute('data-pf-count');
      var target = parseFloat(String(raw).replace(',', '.'));
      if (isNaN(target)) return; // lege/flag-vorm zonder waarde -> niets te doen
      el.dataset.pfCountInit = '1';

      // Suffix/prefix: expliciet via attribuut, anders uit huidige tekst afgeleid.
      var current = (el.textContent || '').trim();
      var prefix = el.getAttribute('data-pf-count-prefix');
      var suffix = el.getAttribute('data-pf-count-suffix');
      var numStr = String(raw);
      var decimals = el.hasAttribute('data-pf-count-decimals')
        ? (parseInt(el.getAttribute('data-pf-count-decimals'), 10) || 0)
        : ((numStr.split('.')[1] || '').length);

      if (prefix === null || suffix === null) {
        // Leid af uit de bestaande tekst: alles vóór/na het cijferblok.
        var m = current.match(/[-+]?[\d.,]*\d/);
        if (m) {
          var idx = current.indexOf(m[0]);
          if (prefix === null) prefix = current.slice(0, idx);
          if (suffix === null) suffix = current.slice(idx + m[0].length);
        }
      }
      prefix = prefix || '';
      suffix = suffix || '';

      var duration = parseInt(el.getAttribute('data-pf-count-duration'), 10);
      if (isNaN(duration)) duration = 1600;
      // NL-notatie: punt->komma als decimalen, geen duizendtal-injectie.
      function fmt(v) {
        var s = v.toFixed(decimals);
        return prefix + s.replace('.', ',') + suffix;
      }

      if (REDUCED) { el.textContent = fmt(target); return; }
      el.textContent = fmt(0);

      function run(t) {
        var start = null;
        function tick(ts) {
          if (start === null) start = ts;
          var p = Math.min(1, (ts - start) / duration);
          var eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
          t.textContent = fmt(target * eased);
          if (p < 1) requestAnimationFrame(tick);
          else t.textContent = fmt(target);
        }
        requestAnimationFrame(tick);
      }
      onInView([el], run, '0px 0px -60px 0px');
    });
  }

  /* ---------- 4) Gestaggerde groep-reveal: data-pf-stagger --------- */
  function initStagger(root) {
    var groups = (root || document).querySelectorAll('[data-pf-stagger]');
    groups.forEach(function (el) {
      if (el.dataset.pfStaggerInit) return;
      el.dataset.pfStaggerInit = '1';

      if (REDUCED) { el.classList.add('is-visible', 'is-done'); return; }

      var step = parseInt(el.getAttribute('data-pf-stagger-step'), 10);
      if (isNaN(step)) step = 90; // ms tussen children
      var children = el.children;
      for (var i = 0; i < children.length; i++) {
        children[i].style.setProperty('--pf-stagger-delay', (i * step) + 'ms');
      }

      onInView([el], function (t) {
        t.classList.add('is-visible');
        var kids = t.children;
        var lastKid = kids[kids.length - 1];
        if (lastKid) {
          lastKid.addEventListener('transitionend', function once() {
            t.classList.add('is-done');
            lastKid.removeEventListener('transitionend', once);
          });
        } else {
          t.classList.add('is-done');
        }
      });
    });
  }

  /* ---------- Init + Shopify section re-render --------------------- */
  function initAll(root) {
    initWords(root);
    initMagnet(root);
    initCount(root);
    initStagger(root);
    // .pf-hover-zoom is pure CSS — geen JS nodig.
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Shopify theme editor: re-init alleen de zojuist (her)geladen sectie.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target || document);
  });
})();
